import * as React from 'react';
import { Badge, Button } from '@/components/ui';
import { BEAT_CHANGE_REASON_MAX_LENGTH, type BeatRef } from '@/hooks/useMyBeatToday';
import { cn } from '@/lib/utils';

/**
 * Only ever the signed-in executive's own beats — `allowedBeatPool` already
 * comes scoped that way from the server, never every company beat.
 *
 * Two steps in one sheet rather than an immediate tap-to-submit: picking a
 * beat now only selects it, so a required reason can be collected before
 * anything is sent. Both the selection and whatever's typed survive a failed
 * submit attempt — nothing here resets on validation failure.
 *
 * Shared by MyBeatTodayPage and the AppShell top bar — one component, one
 * Beat-change request flow, so there is no second state machine to drift
 * from the Admin-approval rules the backend enforces.
 */
export function BeatChangeSheet({
  pool,
  activeBeatId,
  pending,
  onCancel,
  onSubmit,
}: {
  pool: BeatRef[];
  activeBeatId: string | null;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (routeId: string, reason: string) => void;
}) {
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [reason, setReason] = React.useState('');
  const [touched, setTouched] = React.useState(false);

  const trimmedReason = reason.trim();
  const reasonMissing = trimmedReason === '';
  const showReasonError = touched && reasonMissing;
  const canSubmit = selectedId !== null && !reasonMissing;

  function handleSubmit() {
    setTouched(true);
    if (selectedId === null || reasonMissing) return;
    onSubmit(selectedId, trimmedReason);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 sm:items-center sm:p-4">
      <div className="flex max-h-[90vh] w-full max-w-md flex-col rounded-t-2xl border border-border bg-background shadow-xl sm:rounded-2xl">
        <div className="shrink-0 border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold">Request a beat change</h2>
          <p className="text-xs text-muted-foreground">Only beats already assigned to you are shown.</p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="space-y-1.5">
            {pool.length === 0 ? (
              <p className="text-sm text-muted-foreground">No beats are assigned to you yet.</p>
            ) : (
              pool.map((beat) => {
                const isActive = beat.id === activeBeatId;
                const isSelected = beat.id === selectedId;

                return (
                  <button
                    key={beat.id}
                    type="button"
                    disabled={isActive || pending}
                    onClick={() => setSelectedId(beat.id)}
                    className={cn(
                      'flex w-full items-center justify-between rounded-md border px-3 py-2.5 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 enabled:hover:bg-muted',
                      isSelected ? 'border-primary bg-primary/5' : 'border-border',
                    )}
                  >
                    <span>
                      <span className="font-medium">{beat.name}</span>
                      {beat.areaName && <span className="ml-1.5 text-xs text-muted-foreground">· {beat.areaName}</span>}
                    </span>
                    {isActive ? (
                      <Badge tone="neutral">Current</Badge>
                    ) : isSelected ? (
                      <Badge tone="info">Selected</Badge>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>

          {pool.length > 0 && (
            <div className="mt-4">
              <label htmlFor="beat-change-reason" className="mb-1 block text-sm font-medium">
                Reason for change <span className="text-destructive">*</span>
              </label>
              <textarea
                id="beat-change-reason"
                rows={3}
                maxLength={BEAT_CHANGE_REASON_MAX_LENGTH}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                onBlur={() => setTouched(true)}
                disabled={pending}
                placeholder="Enter reason for changing today's beat"
                aria-invalid={showReasonError}
                aria-describedby={showReasonError ? 'beat-change-reason-error' : undefined}
                className={cn(
                  'flex w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  showReasonError ? 'border-destructive' : 'border-input',
                )}
              />
              <div className="mt-1 flex items-center justify-between gap-2 text-xs">
                {showReasonError ? (
                  <p id="beat-change-reason-error" className="font-medium text-destructive">
                    Reason is required.
                  </p>
                ) : (
                  <span />
                )}
                <span className="shrink-0 text-muted-foreground">
                  {reason.length}/{BEAT_CHANGE_REASON_MAX_LENGTH}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="flex shrink-0 gap-2 border-t border-border p-4">
          <Button type="button" variant="outline" className="flex-1" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button type="button" className="flex-1" onClick={handleSubmit} disabled={pending || !canSubmit}>
            Submit request
          </Button>
        </div>
      </div>
    </div>
  );
}
